/**
 * Utility function to safely read CSS variables in both client and server environments.
 * @param variable - The CSS variable name (e.g., '--primary')
 * @param fallback - The fallback value if the variable is not found
 * @returns The CSS variable value or the fallback
 */
const getCSSVar = (variable: string, fallback = ''): string => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  return v || fallback;
};

/**
 * Generates the Ant Design theme configuration based on CSS variables.
 * This function reads CSS variables from the document and maps them to Ant Design tokens.
 * The theme automatically adapts to light/dark mode through CSS variables.
 *
 * @returns The complete Ant Design theme configuration object
 */
export const getAntdThemeConfig = () => {
  const primary = getCSSVar('--primary', '#343434');
  const primaryFg = getCSSVar('--primary-foreground', '#fbfbfb');

  const bg = getCSSVar('--background', '#ffffff');
  const fg = getCSSVar('--foreground', '#252525');

  const border = getCSSVar('--border', '#ebebeb');
  const inputBg = getCSSVar('--input', '#ebebeb');
  const ring = getCSSVar('--ring', '#b5b5b5');

  const mutedFg = getCSSVar('--muted-foreground', '#8e8e8e');
  const secondary = getCSSVar('--secondary', '#f8f8f8');
  const secondaryFg = getCSSVar('--secondary-foreground', '#252525');

  const disabled = getCSSVar('--disabled', '#d4d4d4');
  const disabledFg = getCSSVar('--disabled-foreground', '#a3a3a3');

  const elevated = getCSSVar('--elevated', bg);
  const elevatedFg = getCSSVar('--elevated-foreground', fg);

  const modalBg = getCSSVar('--modal-background', '#1a1a1a');
  const modalFg = getCSSVar('--modal-foreground', '#fbfbfb');
  const modalMutedFg = getCSSVar('--modal-muted-foreground', 'rgba(255,255,255,0.7)');

  const tableBodyBg = getCSSVar('--table-body-bg', '#ffffff');
  const tableBodyFg = getCSSVar('--table-body-foreground', '#252525');

  const sidebarBg = getCSSVar('--sidebar', '#ffffff');
  const sidebarFg = getCSSVar('--sidebar-foreground', fg);
  const sidebarPrimary = getCSSVar('--sidebar-primary', primary);
  const sidebarPrimaryFg = getCSSVar('--sidebar-primary-foreground', primaryFg);

  const transparent = 'transparent';

  // Colores fijos para botones (no cambian con el tema)
  const buttonPrimary = '#CCA349';
  const buttonPrimaryText = '#F6F6F6';
  const buttonDefaultBorder = '#1a1a1a';
  const buttonDefaultHoverBg = '#a3a3a3';

  return {
    // No algorithm specified - CSS variables handle light/dark mode
    token: {
      colorPrimary: primary,
      colorInfo: primary,

      colorBgBase: bg,
      colorTextBase: fg,
      colorText: fg,
      colorTextSecondary: mutedFg,
      colorTextTertiary: mutedFg,
      colorTextQuaternary: mutedFg,

      colorBorder: border,
      colorBgContainer: bg,
      colorBgElevated: elevated,
      colorBgLayout: bg,

      fontFamily: 'Europa, sans-serif',
      fontFamilyCode: 'Europa, monospace',
      fontSize: 16,
      fontSizeBase: 16,
      lineHeight: 1.5,

      controlHeight: 40,
      borderRadius: 8,
    },

    components: {
      Typography: {
        fontFamily: 'Europa, sans-serif',
        titleFontFamily: 'Fustat, serif',
        colorText: fg,
        colorTextHeading: fg,
        colorTextDescription: mutedFg,
      },

      Button: {
        fontFamily: 'Europa, sans-serif',

        colorPrimary: buttonPrimary,
        colorPrimaryHover: buttonPrimary,
        colorPrimaryActive: buttonPrimary,
        colorTextLightSolid: buttonPrimaryText,
        primaryColor: buttonPrimaryText,
        colorPrimaryBorder: buttonPrimary,

        // Tokens adicionales para texto del botón primario
        colorPrimaryText: buttonPrimaryText,
        colorPrimaryTextHover: buttonPrimaryText,
        colorPrimaryTextActive: buttonPrimaryText,

        colorBgContainerDisabled: disabled,
        colorTextDisabled: disabledFg,
        borderColorDisabled: disabled,

        defaultBg: bg,
        defaultColor: fg,
        defaultBorderColor: buttonDefaultBorder,
        defaultHoverBg: buttonDefaultHoverBg,
        defaultHoverColor: fg,
        defaultHoverBorderColor: buttonDefaultBorder,

        // Estilos para botones de icono
        colorIcon: fg,
        colorIconHover: buttonPrimary,
      },

      Input: {
        fontFamily: 'Europa, sans-serif',
        colorBgContainer: inputBg,
        colorBorder: border,
        colorText: fg,
        colorTextPlaceholder: mutedFg,
        colorPrimaryHover: ring,
        controlOutline: 'none',
        controlOutlineWidth: 0,
        activeColorBorder: primary,
        activeBg: inputBg,
      },

      Select: {
        fontFamily: 'Europa, sans-serif',
        colorBgContainer: inputBg,
        colorBorder: border,
        colorText: fg,
        colorTextPlaceholder: mutedFg,
        controlOutline: 'none',
        controlOutlineWidth: 0,
        colorBgElevated: bg,
        optionSelectedColor: fg,
        colorPrimary: border,
        activeBorderColor: border,
        hoverBorderColor: border,
        optionActiveBg: secondary,
        optionSelectedBg: secondary,
      },

      DatePicker: {
        fontFamily: 'Europa, sans-serif',
        colorBgContainer: inputBg,
        colorBorder: border,
        colorText: fg,
        colorTextPlaceholder: mutedFg,
        controlOutline: 'none',
        controlOutlineWidth: 0,
        cellActiveWithRangeBg: primary,
        cellHoverBg: secondary,
      },

      Tabs: {
        fontFamily: 'Europa, sans-serif',
        colorPrimary: primary,
        colorText: fg,
        inkBarColor: primary,
        itemColor: fg,
        itemSelectedColor: primary,
        itemHoverColor: primary,
        backgroundColor: tableBodyBg,
      },

      Tooltip: {
        fontFamily: 'Europa, sans-serif',

        colorBgSpotlight: elevatedFg,
        colorTextLightSolid: elevated,
        borderRadius: 6,
      },

      Modal: {
        fontFamily: 'Europa, sans-serif',
        contentBg: modalBg,
        headerBg: modalBg,
        colorText: modalFg,
        titleColor: modalFg,
        colorBgElevated: modalBg,
        colorIcon: modalFg,
        colorIconHover: modalMutedFg,
        borderRadiusLG: 10,
      },

      Menu: {
        itemBg: sidebarBg,
        itemColor: sidebarFg,

        itemHoverBg: 'transparent',
        itemHoverColor: sidebarFg,

        itemSelectedBg: sidebarPrimary,
        itemSelectedColor: sidebarPrimaryFg,

        popupBg: sidebarBg,
        colorText: sidebarFg,
      },

      Table: {
        fontFamily: 'Europa, sans-serif',
        headerBg: primary,
        headerColor: primaryFg,
        headerSortActiveBg: primary,
        headerSortHoverBg: primary,
        colorBgContainer: tableBodyBg,
        colorText: tableBodyFg,
        borderColor: border,
        rowHoverBg: tableBodyBg,
        bodySortBg: tableBodyBg,
      },

      Tag: {
        fontFamily: 'Europa, sans-serif',
        colorText: fg,
        colorBgContainer: secondary,
        colorBorder: transparent,
      },

      Collapse: {
        fontFamily: 'Europa, sans-serif',
        colorText: fg,
        colorBorder: border,
        headerBg: bg,
        contentBg: bg,
      },

      Breadcrumb: {
        fontFamily: 'Europa, sans-serif',
        itemColor: mutedFg,
        lastItemColor: fg,
        linkColor: mutedFg,
        linkHoverColor: fg,
        separatorColor: mutedFg,
      },

      Skeleton: {
        colorFill: secondary,
        colorFillContent: secondaryFg,
      },
    },
  };
};
